<template>
  <ProductCardV2
    :product="product"
    :layout="layout"
    :size="inSlider ? 'responsive' : 'default'"
    :context="context"
    :tone="tone"
    :typography="typography"
    :is-on-wishlist="isOnWishlist"
    :disable-wishlist="disableWishlist"
    :qid="qid"
    :open-in-new-tab="openInNewTab"
    :before-navigation-callback="beforeNavigationCallback"
    :before-same-tab-location-assign="beforeSameTabLocationAssign"
    :image-loading="imageLoading"
    :is-special-giftcard="isSpecialGiftcard"
  />
</template>

<script setup lang="ts">
import type { AlgoliaEventItem } from '~/composables/types/algolia'
import type {
  ProductCardContext,
  ProductCardTone,
  ProductCardTypography,
} from '~/utils/productCardMapper'
import ProductCardV2 from '~/components/product/ProductCardV2.vue'

/**
 * Picks the V2 layout a showcase needs:
 * - rails: the grid card, compact below `md`
 * - single-column showcases: the list row
 * - grids: the list row below `md`, the grid card from `md`
 *
 * It's all CSS breakpoints inside ProductCardV2, so SSR renders the same
 * markup the client hydrates.
 *
 * No `h-full` here: a percentage height on a flex item stops it from
 * stretching, so a row with a shorter card would leave that card short.
 * Parents stretch the card instead.
 */
const {
  product,
  context = 'generic',
  tone = 'default',
  typography = 'standard',
  isOnWishlist = false,
  disableWishlist = false,
  qid = null,
  openInNewTab = false,
  beforeNavigationCallback = undefined,
  beforeSameTabLocationAssign = undefined,
  imageLoading = 'lazy',
  inSlider = false,
  isSingleColumn = false,
  isSpecialGiftcard = false,
} = defineProps<{
  product: AlgoliaEventItem
  context?: ProductCardContext
  tone?: ProductCardTone
  typography?: ProductCardTypography
  isOnWishlist?: boolean
  disableWishlist?: boolean
  qid?: string | null
  openInNewTab?: boolean
  beforeNavigationCallback?: () => void
  beforeSameTabLocationAssign?: () => void
  imageLoading?: '' | 'lazy' | 'eager'
  inSlider?: boolean
  isSingleColumn?: boolean
  isSpecialGiftcard?: boolean
}>()

const layout = computed(() => {
  if (inSlider) return 'card'
  return isSingleColumn ? 'list' : 'auto'
})
</script>
